"use client";

import { useState } from "react";
import type { CUserAccountsContent } from "@/lib/api/cms-catalog";
import type { UserProfile } from "@/lib/api/user-profile";
import type { FrontendUserSession } from "@/lib/api/session";
import {
  Field,
  Input,
  FormStatus,
} from "@/components/ui/Form";
import { Icon } from "@/lib/icons/Icon";
import { TabHeader } from "./TabHeader";
import styles from "./CUserAccounts.module.css";
import { ChangeEmailModal } from "./ChangeEmailModal";
import { DeleteAccountModal } from "./DeleteAccountModal";

/**
 * ProfileTabAccount — zakładka „Konto":
 *   - Email (read-only) z buttonem „Zmień adres email" -> modal
 *   - Pending change indicator (gdy tmp_data.change_email aktywne)
 *   - Danger zone "Usuń konto" (gated po `config.option_delete_account`
 *     ORAZ NIE jest adminem) -> modal
 *
 * **Admin block:** sekcja „Strefa niebezpieczna" jest niewidoczna dla userów
 * z `userSession.isAdmin=true`. Backend dodatkowo blokuje admin (`type==2`)
 * w `IAPI_initDeleteAccount` i `IAPI_deleteAccount` (2002/2007) — UI hide
 * to defense in depth + clean UX (admin nie powinien widzieć opcji, której
 * nie może użyć).
 *
 * **Zmiana emaila:** 8-warstwowy security flow (zob. backend `IAPI_initChangeEmail`).
 * **Usunięcie konta:** analogiczny flow — re-auth password + email z confirm/cancel linkami.
 */
export function ProfileTabAccount({
  content,
  profile,
  userSession,
  onProfileRefresh,
}: {
  content: CUserAccountsContent;
  profile: UserProfile;
  userSession: FrontendUserSession | null;
  onProfileRefresh: () => void;
}) {
  const [changeEmailOpen, setChangeEmailOpen] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);

  /* Pending change wykrywany przez SSR-loaded profile (zob. IAPI_user). Backend
     mógłby też wystawić expiry_date — dla MVP wystarczy boolean flag. */
  const hasPendingChange = !!profile.tmp_data?.change_email
    && typeof profile.tmp_data.change_email === "object"
    && Object.keys(profile.tmp_data.change_email).length > 0;

  /* Pending delete — analogicznie do change_email. Renderujemy ostrzeżenie
     "sprawdź skrzynkę" + link żeby user nie inicjował drugi raz. */
  const hasPendingDelete = !!profile.tmp_data?.delete_account
    && typeof profile.tmp_data.delete_account === "object"
    && Object.keys(profile.tmp_data.delete_account).length > 0;

  return (
    <>
      <TabHeader icon="gear" title="Konto" />
      <div className={styles.form}>

      <Field
        label="Adres email"
        hint="Zmiana adresu email wymaga potwierdzenia obecnym hasłem oraz kliknięcia linku weryfikacyjnego wysłanego na nowy adres. Ostrzeżenie zostanie też wysłane na obecny adres — możesz anulować procedurę gdyby ktoś przejął Twoje konto."
      >
        <Input
          type="email"
          value={profile.email ?? ""}
          readOnly
          disabled
          aria-readonly
        />
      </Field>

      <div className={styles.changeEmailActions}>
        <button
          type="button"
          className={styles.linkButton}
          onClick={() => setChangeEmailOpen(true)}
        >
          <Icon name="pencil-simple" /> Zmień adres email
        </button>
      </div>

      {hasPendingChange && (
        <FormStatus variant="warning">
          Pending zmiana adresu email — sprawdź skrzynkę nowego adresu, kliknij
          link weryfikacyjny w ciągu 1 godziny. Stary adres dostał ostrzeżenie
          z linkiem ANULUJ jeśli to nie Ty zainicjowałeś zmianę.
        </FormStatus>
      )}

      {content.config.option_delete_account && !userSession?.isAdmin && (
        <div className={styles.dangerZone}>
          <h3 className={styles.dangerZoneTitle}>
            <Icon name="warning" aria-hidden /> Strefa niebezpieczna — usunięcie konta
          </h3>
          <p className={styles.dangerZoneText}>
            Usunięcie konta jest <strong>nieodwracalne</strong>. Utracisz dostęp do wszystkich
            danych, historii zamówień i ustawień zapisanych pod tym kontem. Procedura wymaga
            potwierdzenia obecnym hasłem oraz kliknięcia linku wysłanego na Twój adres email
            (1h ważności). W każdej chwili przed kliknięciem linku możesz anulować procedurę.
          </p>

          {hasPendingDelete ? (
            <FormStatus variant="warning">
              Pending usunięcie konta — sprawdź skrzynkę, kliknij <strong>POTWIERDŹ USUNIĘCIE</strong>{" "}
              lub <strong>ANULUJ</strong> w mailu (1h ważności). Jeśli nie zareagujesz — procedura
              automatycznie wygaśnie.
            </FormStatus>
          ) : (
            <button
              type="button"
              className={styles.dangerButton}
              onClick={() => setDeleteAccountOpen(true)}
            >
              <Icon name="trash" aria-hidden /> Usuń moje konto
            </button>
          )}
        </div>
      )}

      {changeEmailOpen && (
        <ChangeEmailModal
          currentEmail={profile.email ?? ""}
          onClose={() => setChangeEmailOpen(false)}
          onSuccess={() => {
            setChangeEmailOpen(false);
            onProfileRefresh();
          }}
        />
      )}

      {deleteAccountOpen && (
        <DeleteAccountModal
          currentEmail={profile.email ?? ""}
          onClose={() => setDeleteAccountOpen(false)}
          onSuccess={() => {
            setDeleteAccountOpen(false);
            onProfileRefresh();
          }}
        />
      )}
      </div>
    </>
  );
}
